import { Component, h, Host, Prop } from '@stencil/core';
import { Element, HostElement, HTMLStencilElement, State } from '@stencil/core/internal';
import { WindowResizeService } from '../../services/window-resize-service';
import { ResponsiveString } from '../../typings';

/**
 * @slot content - Inner contents of the button, usually plaintext.
 * @slot - Default slot, same as content slot.
 * @slot activatedContent - If this slot is used, the content in this will be scrolled to and triggered.
 */
@Component({
  tag: 'content-button',
  styleUrl: 'content-button.scss',
  shadow: false,
})
export class ContentButton {
  @Element() hostElement: HostElement;
  /**
   * @type URL
   */
  @Prop({
    mutable: true,
    reflect: true,
  })
  href: string;
  /**
   * @type String
   */
  @Prop() target: string;
  /**
   * The classes for the button.
   * @type String
   * @responsive
   */
  @Prop() classes = '';
  @State() classString = '';
  /**
   * @type Boolean
   */
  @Prop() clickableBlock: boolean = false;
  /**
   * @type Boolean
   */
  @Prop({
    mutable: true,
    reflect: true,
  })
  expanded: string;
  /**

   /**
    * Aria-controls value
   * @type Target
   */
  @Prop() controls: string;

  /**
   * @type String
   * @responsive
   */
  @Prop() linkLabel: ResponsiveString;
  /**
   * @type String
   * @responsive
   */
  @Prop({
    mutable: true,
    reflect: true,
  })
  linkClass: ResponsiveString;
  @State() hasContentToActivate: boolean;

  public activatedContent: HTMLDivElement;

  componentWillLoad() {
    this.activatedContent = this.hostElement.querySelector('[slot="activatedContent"]');
    this.hasContentToActivate = !!this.activatedContent;
    // If this is a clickable block, forcefully add a # to force an anchor link
    if (!this.href || this.href === '#') {
      this.href = 'javascript:void(0);';
    }

    WindowResizeService.registerProp(this.linkClass).subscribe(newValue => {
      this.linkClass = newValue as string;
    });

    WindowResizeService.registerProp(this.classes).subscribe(newValue => {
      this.classString = newValue as string;
    });
  }

  onHandleClickEvent(ev) {
    if (this.hasContentToActivate) {
      ev.preventDefault();
      // ev.stopPropagation();
      this.activatedContent.dispatchEvent(new CustomEvent('open', { detail: this.activatedContent }));
      setTimeout(() => {
        this.activatedContent.scrollIntoView({ behavior: 'smooth' });
      }, 0);
    } else if (this.href && this.href.length > 1 && this.href.substring(0, 1) === '#') {
      let element = document.getElementById(this.href.substring(1));
      ev.preventDefault();
      if (element) {
        ev.preventDefault();
        // ev.stopPropagation();
        element.dispatchEvent(new CustomEvent('open', { detail: element }));
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 0);
      }
    }
  }

  render() {
    return (
      <Host class={this.classString}>
        <a
          aria-label={this.linkLabel}
          class={this.linkClass}
          role={this.href === 'javascript:void(0);' ? 'button' : ''}
          onClick={ev => this.onHandleClickEvent(ev)}
          aria-expanded={this.expanded}
          aria-controls={this.controls}
          href={this.href}
          target={this.target}
        >
          {!this.clickableBlock ? (
            <span>
              <slot name="content"></slot>
              <slot></slot>
            </span>
          ) : (
            <div class="content-button__clickable-content">
              <slot name="content"></slot>
              <slot></slot>
            </div>
          )}
        </a>
        <slot name="activatedContent"></slot>
      </Host>
    );
  }
}
